import React, { useEffect, useState } from 'react';
import { CreditCard, Calendar, CheckCircle2, XCircle, PlusCircle, Trash2, Power, X } from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { EduBadge } from '../components/EduBadge.js';
import { EduButton } from '../components/EduButton.js';
import { api } from '../services/api.js';
import { Subscription, Customer, Plan } from '../types/index.js';

export const Subscriptions: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal create subscription state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [provider, setProvider] = useState<'manual' | 'pagarme' | 'mercadopago' | 'stripe'>('manual');
  const [submitting, setSubmitting] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [subsRes, custsRes, plansRes] = await Promise.all([
        api.getSubscriptions(),
        api.getCustomers().catch(() => ({ items: [] })),
        api.getPlans().catch(() => ({ items: [] })),
      ]);
      setSubscriptions(subsRes.items || []);
      setCustomers(custsRes.items || []);
      setPlans(plansRes.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenCreateModal = () => {
    if (customers.length > 0 && !selectedCustomerId) {
      setSelectedCustomerId(customers[0].id);
    }
    if (plans.length > 0 && !selectedPlanId) {
      setSelectedPlanId(plans[0].id);
    }
    setStartDate(new Date().toISOString().split('T')[0]);
    setIsModalOpen(true);
  };

  const handleCreateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomerId || !selectedPlanId) {
      alert('Selecione o cliente e o plano para a assinatura.');
      return;
    }

    setSubmitting(true);
    try {
      await api.createSubscription({
        customerId: selectedCustomerId,
        planId: selectedPlanId,
        startDate: startDate ? new Date(startDate).toISOString() : new Date().toISOString(),
        provider,
      });
      alert('Assinatura criada com sucesso!');
      setIsModalOpen(false);
      loadData();
    } catch (err: any) {
      alert(err.message || 'Erro ao criar assinatura.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await api.activateSubscription(id);
      loadData();
    } catch (err: any) {
      alert(err.message || 'Erro ao ativar assinatura.');
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Deseja realmente desativar esta assinatura?')) return;
    try {
      await api.deactivateSubscription(id);
      loadData();
    } catch (err: any) {
      alert(err.message || 'Erro ao desativar assinatura.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Deseja realmente excluir este registro de assinatura?')) return;
    try {
      await api.deleteSubscription(id);
      alert('Assinatura excluída com sucesso!');
      loadData();
    } catch (err: any) {
      alert(err.message || 'Erro ao excluir assinatura.');
    }
  };

  const getCustomerLabel = (customerId: string) => {
    const found = customers.find((c) => c.id === customerId);
    if (!found) return customerId;
    return found.legalName || found.fullName || found.tradeName || found.email;
  };

  const getCustomerEmail = (customerId: string) => {
    const found = customers.find((c) => c.id === customerId);
    return found?.email || '';
  };

  return (
    <div>
      <Header
        title="Gestão de Assinaturas"
        subtitle="Acompanhamento dos ciclos contratuais, datas de renovação e status de cada tenant"
        action={
          <EduButton
            variant="primary"
            size="sm"
            onClick={handleOpenCreateModal}
            icon={<PlusCircle size={16} />}
          >
            Nova Assinatura
          </EduButton>
        }
      />

      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <EduCard title="Assinaturas Contratadas">
          {loading ? (
            <div className="p-12 text-center text-xs text-txt-muted">Carregando assinaturas...</div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-10 text-xs text-txt-secondary">
              Nenhuma assinatura cadastrada até o momento.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-bdr text-txt-secondary font-semibold bg-surface-secondary">
                    <th className="py-3 px-4">Cliente / Instituição</th>
                    <th className="py-3 px-4">Plano</th>
                    <th className="py-3 px-4">Valor Mensal</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Início</th>
                    <th className="py-3 px-4">Próxima Renovação</th>
                    <th className="py-3 px-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-bdr text-txt-primary">
                  {subscriptions.map((sub) => {
                    const custName = getCustomerLabel(sub.customerId);
                    const custEmail = getCustomerEmail(sub.customerId);

                    return (
                      <tr key={sub.id} className="hover:bg-surface-secondary/40">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-txt-primary">{custName}</div>
                          {custEmail && <div className="text-[11px] text-txt-muted font-mono">{custEmail}</div>}
                        </td>
                        <td className="py-3 px-4 font-semibold text-brand-primary">
                          {sub.planSnapshot?.name || sub.planId}
                        </td>
                        <td className="py-3 px-4 font-mono">
                          R$ {((sub.planSnapshot?.monthlyPrice || 19900) / 100).toFixed(2).replace('.', ',')}
                        </td>
                        <td className="py-3 px-4">
                          {sub.status === 'active' ? (
                            <EduBadge variant="success" dot>Ativa</EduBadge>
                          ) : sub.status === 'inactive' ? (
                            <EduBadge variant="neutral" dot>Inativa</EduBadge>
                          ) : sub.status === 'suspended' ? (
                            <EduBadge variant="warning" dot>Suspensa</EduBadge>
                          ) : (
                            <EduBadge variant="error" dot>Cancelada</EduBadge>
                          )}
                        </td>
                        <td className="py-3 px-4 font-mono text-txt-secondary">
                          {new Date(sub.startDate).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-3 px-4 font-mono font-semibold text-txt-primary">
                          {new Date(sub.nextBillingDate).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {sub.status === 'active' ? (
                              <EduButton
                                variant="secondary"
                                size="sm"
                                onClick={() => handleDeactivate(sub.id)}
                                icon={<Power size={12} className="text-amber-600" />}
                              >
                                Desativar
                              </EduButton>
                            ) : (
                              <EduButton
                                variant="primary"
                                size="sm"
                                onClick={() => handleActivate(sub.id)}
                                icon={<Power size={12} />}
                              >
                                Ativar
                              </EduButton>
                            )}

                            <EduButton
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(sub.id)}
                              icon={<Trash2 size={12} />}
                            >
                              Excluir
                            </EduButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </EduCard>
      </div>

      {/* Modal Nova Assinatura */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl border border-bdr shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-txt-primary">Cadastrar Nova Assinatura</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-txt-muted hover:text-txt-primary">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateSubscription} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Cliente (Tenant) *
                </label>
                <select
                  required
                  value={selectedCustomerId}
                  onChange={(e) => setSelectedCustomerId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md bg-surface text-txt-primary focus:outline-none focus:border-brand-primary"
                >
                  <option value="">Selecione um cliente...</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.legalName || c.fullName || c.tradeName || c.email} ({c.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Plano Contratado *
                </label>
                <select
                  required
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md bg-surface text-txt-primary focus:outline-none focus:border-brand-primary"
                >
                  <option value="">Selecione um plano...</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — R$ {(p.monthlyPrice / 100).toFixed(2).replace('.', ',')} / mês ({p.monthlyLessonLimit} aulas)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Data de Início *
                </label>
                <input
                  type="date"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md bg-surface text-txt-primary focus:outline-none focus:border-brand-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-txt-primary mb-1">
                  Método / Provedor de Pagamento
                </label>
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as any)}
                  className="w-full px-3 py-2 text-sm border border-bdr rounded-md bg-surface text-txt-primary focus:outline-none focus:border-brand-primary"
                >
                  <option value="manual">Faturamento Direto / Manual (Boleto / Pix)</option>
                  <option value="pagarme">Pagar.me</option>
                  <option value="mercadopago">Mercado Pago</option>
                  <option value="stripe">Stripe</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <EduButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancelar
                </EduButton>
                <EduButton
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={submitting}
                >
                  Criar Assinatura
                </EduButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
